/* View in your room — true-size wall AR from the live frame visualiser.
   Keep PRINT_SIZES / FRAME_WIDTH_CM / MOUNT_WIDTH_CM / MULTI_PANEL_SPECS in step with theme.js. */
(function () {
  "use strict";

  var MODEL_VIEWER_SRC = "https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js";
  var DEPTH_M = 0.018;
  var TEXTURE_MAX = 1024;
  var TEXTURE_MAX_MULTI = 1536;

  var PRINT_SIZES = {
    square: {
      small: { w: 40, h: 40 },
      medium: { w: 50, h: 50 },
      large: { w: 60, h: 60 },
      extralarge: { w: 75, h: 75 }
    },
    landscape: {
      small: { w: 55, h: 40 },
      medium: { w: 65, h: 48 },
      large: { w: 80, h: 60 },
      extralarge: { w: 100, h: 70 }
    }
  };
  var ROSENSTIELS_PRINT_SIZES = {
    square: {
      small: { w: 40, h: 40 },
      medium: { w: 50, h: 50 },
      large: { w: 75, h: 75 },
      extralarge: { w: 90, h: 90 }
    },
    landscape: {
      small: { w: 50, h: 40 },
      medium: { w: 75, h: 60 },
      large: { w: 95, h: 75 },
      extralarge: { w: 120, h: 95 }
    }
  };
  var FRAME_WIDTH_CM = {
    standard: 2.0,
    widebox: 2.0,
    swoop: 3.5,
    venice: 3.0
  };
  var MOUNT_WIDTH_CM = {
    small: 4.5,
    medium: 5.5,
    large: 7.0,
    extralarge: 8.0
  };
  var FINISH = {
    oak: "#9b7a4d",
    black: "#121110",
    white: "#dad4ca",
    gold: "#b8934e",
    silver: "#aeb4b9",
    bronze: "#7c5c34",
    natural: "#cbb68e",
    "antique-gold": "#93753f",
    "antique-silver": "#98968c",
    "black-pewter": "#1a1a1c",
    "terracotta-silver": "#b5673f",
    "white-silver": "#e0dbd2"
  };
  var MULTI_PANEL_SPECS = {
    diptych: {
      panels: 2,
      gapCm: 5,
      mountCm: 2.5,
      panel: { w: 47.5, h: 100 },
      overall: { w: 100, h: 100 }
    },
    triptych: {
      panels: 3,
      gapCm: 5,
      mountCm: 2.5,
      panel: { w: 45, h: 100 },
      overall: { w: 145, h: 100 }
    }
  };
  var MOUNT_COLOURS = {
    white: "#FFFFFF",
    "snow white": "#EDF0F0",
    "fresh linen": "#EEE9E3",
    "warm sand": "#E8DABF",
    "french grey": "#C3BCA8",
    olive: "#7E7661",
    black: "#2D2824",
    snow: "#EDF0F0",
    ivory: "#EEE9E3",
    stone: "#C3BCA8",
    charcoal: "#2D2824",
    sage: "#7E7661",
    clay: "#E8DABF"
  };

  var overlay = null;
  var objectUrl = null;
  var lastFocus = null;
  var iosPlace = null;

  function colourKey(val) {
    if (!val) return "";
    var v = String(val).toLowerCase();
    if (v === "antique gold") return "antique-gold";
    if (v === "antique silver") return "antique-silver";
    if (v === "black and pewter") return "black-pewter";
    if (v === "terracotta and silver") return "terracotta-silver";
    if (v === "white and silver") return "white-silver";
    return v.split(" ")[0];
  }

  function typeKey(v) {
    v = String(v || "").toLowerCase();
    if (v.indexOf("swoop") > -1) return "swoop";
    if (v.indexOf("venice") > -1) return "venice";
    if (v.indexOf("wide") > -1 || v.indexOf("box") > -1 || v.indexOf("deep") > -1) return "widebox";
    return "standard";
  }

  function sizeKey(value) {
    var key = String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    if (key.indexOf("diptych") > -1) return "diptych";
    if (key.indexOf("triptych") > -1) return "triptych";
    if (key === "xl" || key.indexOf("extralarge") > -1 || key.indexOf("xlarge") > -1) return "extralarge";
    if (key.indexOf("small") > -1) return "small";
    if (key.indexOf("large") > -1) return "large";
    return "medium";
  }

  function multiPanelOuterCm(spec, frameCm, mountCm) {
    // panel.w / panel.h already include frame and mount. Keep them in the
    // signature so the inset (drawn inside each panel) stays explicit.
    var derivedW = spec.panels * spec.panel.w + (spec.panels - 1) * (spec.gapCm || 0);
    var derivedH = spec.panel.h;
    var overall = spec.overall;
    var overallW = overall && overall.w;
    var overallH = overall && overall.h;
    if (frameCm < 0 || mountCm < 0 || !overall ||
        Math.abs(overallW - derivedW) > 0.05 || Math.abs(overallH - derivedH) > 0.05) {
      throw new Error(
        "Multi-panel spec mismatch: overall " + overallW + "×" + overallH +
        " vs panels " + derivedW + "×" + derivedH
      );
    }
    return { w: overallW, h: overallH, panelW: spec.panel.w, panelH: spec.panel.h };
  }

  function dimensionShape(orientation) {
    var value = String(orientation || "portrait").toLowerCase();
    if (value.indexOf("pano") > -1 && value.indexOf("port") > -1) return "portrait";
    if (value.indexOf("pano") > -1) return null;
    if (value.indexOf("square") > -1) return "square";
    if (value.indexOf("land") > -1) return "landscape";
    return "portrait";
  }

  function parseSizeCmFromLabel(value) {
    var match = String(value || "").match(/(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/i);
    if (!match) return null;
    var w = parseFloat(match[1]);
    var h = parseFloat(match[2]);
    if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null;
    return { w: w, h: h };
  }

  function printSizeCm(shape, tier, label, catalogueEl) {
    var parsed = parseSizeCmFromLabel(label);
    if (!parsed && catalogueEl && tier) {
      var buttons = catalogueEl.querySelectorAll("[data-value]");
      for (var i = 0; i < buttons.length; i++) {
        var value = buttons[i].getAttribute("data-value") || "";
        if (sizeKey(value) !== tier) continue;
        if (sizeKey(value) === "diptych" || sizeKey(value) === "triptych") continue;
        parsed = parseSizeCmFromLabel(value);
        if (parsed) break;
      }
    }
    if (parsed) return parsed;
    var rosen = false;
    var node = catalogueEl;
    while (node && node.getAttribute) {
      if (/rosenstiel/i.test(node.getAttribute("data-catalogue") || "")) {
        rosen = true;
        break;
      }
      node = node.parentElement;
    }
    var table = rosen ? ROSENSTIELS_PRINT_SIZES : PRINT_SIZES;
    if (shape === "portrait") {
      var landscape = table.landscape[tier];
      return { w: landscape.h, h: landscape.w };
    }
    return table[shape][tier];
  }

  function isHandheldAR() {
    var ua = navigator.userAgent || "";
    var iOS = /iPhone|iPod|iPad/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    return iOS || /Android/i.test(ua);
  }

  function isIOSDevice() {
    var ua = navigator.userAgent || "";
    return /iPhone|iPod|iPad/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  }

  function isIOSNotSafari() {
    if (!isIOSDevice()) return false;
    return /CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent || "");
  }

  function visualiserOf(root) {
    if (!root) return null;
    return root.matches("[data-frame-visualiser]") ? root : root.querySelector("[data-frame-visualiser]");
  }

  function productRoot(from) {
    return (from && from.closest("[data-product]")) || document.querySelector("[data-product]");
  }

  function readOptions(visualiser) {
    var api = visualiser && visualiser.frameVisualiser;
    if (api && typeof api.getOptions === "function") return api.getOptions();
    return {
      frameStyle: "Classic",
      colour: "Oak",
      size: "Medium",
      mount: "Mounted",
      mountColour: "Fresh Linen"
    };
  }

  function configuredUrl(visualiser) {
    var api = visualiser && visualiser.frameVisualiser;
    if (api && typeof api.getConfiguredUrl === "function") return api.getConfiguredUrl();
    return location.href.split("#")[0];
  }

  function arHandoffUrl(visualiser) {
    var url = new URL(configuredUrl(visualiser), location.origin);
    url.searchParams.set("ar", "1");
    return url.toString();
  }

  function extractBgImageUrl(styleValue) {
    var match = String(styleValue || "").match(/url\(["']?(.*?)["']?\)/i);
    return match ? match[1] : "";
  }

  function artworkSrc(visualiser) {
    var piece = visualiser.querySelector(".dip-slice, .tri-slice") || visualiser.querySelector("[data-piece]");
    if (piece) {
      var zoom = piece.getAttribute("data-zoom-src");
      if (zoom) return zoom;
      var img = piece.querySelector("img");
      if (img && (img.currentSrc || img.src)) return img.currentSrc || img.src;
      var bg = extractBgImageUrl(piece.style.backgroundImage);
      if (bg) return bg;
    }
    var og = document.querySelector('meta[property="og:image"]');
    return (og && og.getAttribute("content")) || "";
  }

  function specFromVisualiser(visualiser) {
    var options = readOptions(visualiser);
    var sizeLabel = options.size || "Medium";
    var layout = sizeKey(sizeLabel);
    var multi = MULTI_PANEL_SPECS[layout] || null;
    var colour = options.colour || "Oak";
    var frameNone = !colour || /no frame|none|unframed/i.test(colour);
    var mounted = options.mount && !/no mount|none|unmounted/i.test(options.mount) && !frameNone;
    var ftype = typeKey(options.frameStyle);
    var frameCm = frameNone ? 0 : (FRAME_WIDTH_CM[ftype] || FRAME_WIDTH_CM.standard);
    var src = artworkSrc(visualiser);
    if (!src) return { error: "This print needs an image before it can be placed on a wall." };

    if (visualiser.querySelector('[data-dimension-mode="unspecified"]')) {
      return { error: "This work is a single fixed size that is not recorded yet, so it cannot be placed in AR." };
    }

    var outer;
    var mountCm;
    var panelOuter;
    if (multi) {
      var mountSpecCm = multi.mountCm;
      mountCm = mounted ? mountSpecCm : 0;
      outer = multiPanelOuterCm(multi, frameCm, mountSpecCm);
      panelOuter = { w: outer.panelW, h: outer.panelH };
    } else {
      var shape = dimensionShape(visualiser.getAttribute("data-orientation"));
      if (!shape) {
        return { error: "AR is available for portrait, landscape, square, diptych and triptych prints." };
      }
      outer = printSizeCm(shape, layout, sizeLabel, visualiser);
      if (!outer) {
        return { error: "This size cannot be placed in AR yet." };
      }
      if (!mounted && window.theme && typeof window.theme.rosenstielsNoMountSize === "function") {
        var catalogue = visualiser.getAttribute("data-catalogue") || "";
        var productNode = visualiser.closest("[data-product]");
        if (productNode) catalogue += " " + (productNode.getAttribute("data-catalogue") || "");
        if (/rosenstiel/i.test(catalogue)) {
          var noMountOuter = window.theme.rosenstielsNoMountSize(sizeLabel);
          if (noMountOuter) outer = { w: noMountOuter.w, h: noMountOuter.h };
        }
      }
      mountCm = mounted ? (MOUNT_WIDTH_CM[layout] || MOUNT_WIDTH_CM.medium) : 0;
      panelOuter = { w: outer.w, h: outer.h };
    }

    return {
      options: options,
      panels: multi ? multi.panels : 1,
      gapCm: multi ? multi.gapCm : 0,
      panel: multi ? multi.panel : null,
      panelOuterCm: panelOuter,
      outerCm: { w: outer.w, h: outer.h },
      metres: { w: outer.w / 100, h: outer.h / 100, d: DEPTH_M },
      frameNone: frameNone,
      mounted: mounted,
      frameCm: frameCm,
      mountCm: mountCm,
      frameHex: FINISH[colourKey(colour)] || FINISH.oak,
      mountHex: MOUNT_COLOURS[String(options.mountColour || "fresh linen").toLowerCase()] || MOUNT_COLOURS["fresh linen"],
      src: src,
      title: document.querySelector("[data-product] h1")
        ? document.querySelector("[data-product] h1").textContent.replace(/\s+/g, " ").trim()
        : "Framed print"
    };
  }

  function imageSize(img) {
    return {
      w: img.naturalWidth || img.width || 1,
      h: img.naturalHeight || img.height || 1
    };
  }

  function blobToImage(blob) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      var url = URL.createObjectURL(blob);
      img.onload = function () {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error("Could not load the print image"));
      };
      img.src = url;
    });
  }

  function loadImage(src) {
    return fetch(src, { mode: "cors", credentials: "omit" }).then(function (res) {
      if (!res.ok) throw new Error("Could not load the print image");
      return res.blob();
    }).then(function (blob) {
      if (typeof createImageBitmap === "function") return createImageBitmap(blob);
      return blobToImage(blob);
    }).catch(function () {
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = function () { resolve(img); };
        img.onerror = function () { reject(new Error("Could not load the print image")); };
        img.src = src;
      });
    });
  }

  function textureSize(ratio, max) {
    var width = max;
    var height = Math.max(1, Math.round(max / ratio));
    if (height > max) {
      height = max;
      width = Math.max(1, Math.round(max * ratio));
    }
    return { width: width, height: height };
  }

  function drawCover(ctx, img, x, y, w, h) {
    var size = imageSize(img);
    var ir = size.w / Math.max(1, size.h);
    var tr = w / Math.max(1, h);
    var dw;
    var dh;
    var dx;
    var dy;
    if (ir > tr) {
      dh = h;
      dw = h * ir;
      dx = x - (dw - w) / 2;
      dy = y;
    } else {
      dw = w;
      dh = w / ir;
      dx = x;
      dy = y - (dh - h) / 2;
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.restore();
  }

  function drawPanel(ctx, spec, img, destX, destY, destW, destH, sliceIndex) {
    var n = spec.panels || 1;
    var framePx = (spec.frameCm / spec.panelOuterCm.w) * destW;
    var mountPx = (spec.mountCm / spec.panelOuterCm.w) * destW;

    ctx.fillStyle = spec.frameNone ? "#F7F4EE" : spec.frameHex;
    ctx.fillRect(destX, destY, destW, destH);

    if (!spec.frameNone && framePx > 0) {
      ctx.fillStyle = spec.mounted ? spec.mountHex : "#F7F4EE";
      ctx.fillRect(destX + framePx, destY + framePx, destW - framePx * 2, destH - framePx * 2);
    }

    var inset = framePx + mountPx;
    var printX = destX + inset;
    var printY = destY + inset;
    var printW = Math.max(1, destW - inset * 2);
    var printH = Math.max(1, destH - inset * 2);

    if (n < 2) {
      drawCover(ctx, img, printX, printY, printW, printH);
      return;
    }

    var combinedW = n * printW;
    var size = imageSize(img);
    var scale = Math.max(combinedW / size.w, printH / size.h);
    var drawnW = size.w * scale;
    var drawnH = size.h * scale;
    var originX = (combinedW - drawnW) / 2;
    var originY = (printH - drawnH) / 2;
    ctx.save();
    ctx.beginPath();
    ctx.rect(printX, printY, printW, printH);
    ctx.clip();
    ctx.drawImage(img, printX - sliceIndex * printW + originX, printY + originY, drawnW, drawnH);
    ctx.restore();
  }

  function paintModelCanvas(spec, img) {
    var n = spec.panels || 1;
    var atlasW = spec.panelOuterCm.w * n;
    var atlasH = spec.panelOuterCm.h;
    var size = textureSize(atlasW / atlasH, n > 1 ? TEXTURE_MAX_MULTI : TEXTURE_MAX);
    var canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    var ctx = canvas.getContext("2d");
    var panelW = size.width / n;
    var i;
    for (i = 0; i < n; i++) {
      drawPanel(ctx, spec, img, i * panelW, 0, panelW, size.height, i);
    }
    return canvas;
  }

  function paintPreviewCanvas(spec, img) {
    var n = spec.panels || 1;
    if (n < 2) return paintModelCanvas(spec, img);
    var size = textureSize(spec.outerCm.w / spec.outerCm.h, TEXTURE_MAX_MULTI);
    var canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    var ctx = canvas.getContext("2d");
    var pxPerCm = size.width / spec.outerCm.w;
    var panelW = spec.panelOuterCm.w * pxPerCm;
    var panelH = spec.panelOuterCm.h * pxPerCm;
    var gap = spec.gapCm * pxPerCm;
    var i;
    for (i = 0; i < n; i++) {
      drawPanel(ctx, spec, img, i * (panelW + gap), 0, panelW, panelH, i);
    }
    return canvas;
  }

  function canvasPng(canvas) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) {
          reject(new Error("Could not encode the print"));
          return;
        }
        var reader = new FileReader();
        reader.onload = function () { resolve(new Uint8Array(reader.result)); };
        reader.onerror = function () { reject(new Error("Could not encode the print")); };
        reader.readAsArrayBuffer(blob);
      }, "image/png");
    });
  }

  function pad4(bytes, fill) {
    var extra = (4 - (bytes.length % 4)) % 4;
    if (!extra) return bytes;
    var out = new Uint8Array(bytes.length + extra);
    out.set(bytes);
    if (fill) out.fill(fill, bytes.length);
    return out;
  }

  function concat(parts) {
    var total = 0;
    var i;
    for (i = 0; i < parts.length; i++) total += parts[i].length;
    var out = new Uint8Array(total);
    var offset = 0;
    for (i = 0; i < parts.length; i++) {
      out.set(parts[i], offset);
      offset += parts[i].length;
    }
    return out;
  }

  function u32(n) {
    var b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, n, true);
    return b;
  }

  function f32array(values) {
    var out = new Uint8Array(values.length * 4);
    var view = new DataView(out.buffer);
    for (var i = 0; i < values.length; i++) view.setFloat32(i * 4, values[i], true);
    return out;
  }

  function u16array(values) {
    var out = new Uint8Array(values.length * 2);
    var view = new DataView(out.buffer);
    for (var i = 0; i < values.length; i++) view.setUint16(i * 2, values[i], true);
    return out;
  }

  function addBox(mesh, cx, cy, w, h, d, u0, u1) {
    var hw = w / 2;
    var hh = h / 2;
    var edgeU = (u1 - u0) * 0.02;
    var edgeV = 0.02;

    function face(verts, n, uv) {
      var base = mesh.positions.length / 3;
      var i;
      for (i = 0; i < 4; i++) {
        mesh.positions.push(verts[i][0], verts[i][1], verts[i][2]);
        mesh.normals.push(n[0], n[1], n[2]);
        mesh.uvs.push(uv[i][0], uv[i][1]);
      }
      mesh.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }

    face(
      [[cx - hw, cy - hh, d], [cx + hw, cy - hh, d], [cx + hw, cy + hh, d], [cx - hw, cy + hh, d]],
      [0, 0, 1],
      [[u0, 0], [u1, 0], [u1, 1], [u0, 1]]
    );
    face(
      [[cx + hw, cy - hh, 0], [cx - hw, cy - hh, 0], [cx - hw, cy + hh, 0], [cx + hw, cy + hh, 0]],
      [0, 0, -1],
      [[u1, 0], [u0, 0], [u0, 1], [u1, 1]]
    );
    face(
      [[cx - hw, cy + hh, d], [cx + hw, cy + hh, d], [cx + hw, cy + hh, 0], [cx - hw, cy + hh, 0]],
      [0, 1, 0],
      [[u0, 1], [u1, 1], [u1, 1 - edgeV], [u0, 1 - edgeV]]
    );
    face(
      [[cx - hw, cy - hh, 0], [cx + hw, cy - hh, 0], [cx + hw, cy - hh, d], [cx - hw, cy - hh, d]],
      [0, -1, 0],
      [[u0, edgeV], [u1, edgeV], [u1, 0], [u0, 0]]
    );
    face(
      [[cx + hw, cy - hh, d], [cx + hw, cy - hh, 0], [cx + hw, cy + hh, 0], [cx + hw, cy + hh, d]],
      [1, 0, 0],
      [[u1, 0], [u1 - edgeU, 0], [u1 - edgeU, 1], [u1, 1]]
    );
    face(
      [[cx - hw, cy - hh, 0], [cx - hw, cy - hh, d], [cx - hw, cy + hh, d], [cx - hw, cy + hh, 0]],
      [-1, 0, 0],
      [[u0 + edgeU, 0], [u0, 0], [u0, 1], [u0 + edgeU, 1]]
    );

    mesh.min[0] = Math.min(mesh.min[0], cx - hw);
    mesh.min[1] = Math.min(mesh.min[1], cy - hh);
    mesh.min[2] = Math.min(mesh.min[2], 0);
    mesh.max[0] = Math.max(mesh.max[0], cx + hw);
    mesh.max[1] = Math.max(mesh.max[1], cy + hh);
    mesh.max[2] = Math.max(mesh.max[2], d);
  }

  function buildMesh(spec) {
    var mesh = {
      positions: [],
      normals: [],
      uvs: [],
      indices: [],
      min: [Infinity, Infinity, Infinity],
      max: [-Infinity, -Infinity, -Infinity]
    };
    var n = spec.panels || 1;
    var d = spec.metres.d;
    if (n < 2) {
      addBox(mesh, 0, 0, spec.metres.w, spec.metres.h, d, 0, 1);
      return mesh;
    }
    var panelW = spec.panelOuterCm.w / 100;
    var panelH = spec.panelOuterCm.h / 100;
    var gap = spec.gapCm / 100;
    var i;
    for (i = 0; i < n; i++) {
      var cx = (i - (n - 1) / 2) * (panelW + gap);
      addBox(mesh, cx, 0, panelW, panelH, d, i / n, (i + 1) / n);
    }
    return mesh;
  }

  function buildGlb(png, spec) {
    var mesh = buildMesh(spec);
    var pos = f32array(mesh.positions);
    var nrm = f32array(mesh.normals);
    var uv = f32array(mesh.uvs);
    var idx = pad4(u16array(mesh.indices), 0);
    var pngPad = pad4(png, 0);

    var views = [];
    var offset = 0;
    function addView(bytes, target) {
      var view = { buffer: 0, byteOffset: offset, byteLength: bytes.length };
      if (target) view.target = target;
      views.push(view);
      offset += bytes.length;
      return views.length - 1;
    }

    var posView = addView(pos, 34962);
    var nrmView = addView(nrm, 34962);
    var uvView = addView(uv, 34962);
    var idxView = addView(idx, 34963);
    var imgView = addView(pngPad);

    var json = {
      asset: { version: "2.0", generator: "TheArtFolio view-in-room" },
      scene: 0,
      scenes: [{ nodes: [0] }],
      nodes: [{ mesh: 0 }],
      meshes: [{
        primitives: [{
          attributes: { POSITION: 0, NORMAL: 1, TEXCOORD_0: 2 },
          indices: 3,
          material: 0
        }]
      }],
      materials: [{
        name: "framed-print",
        pbrMetallicRoughness: {
          baseColorTexture: { index: 0 },
          metallicFactor: 0,
          roughnessFactor: 0.85
        },
        doubleSided: true
      }],
      textures: [{ source: 0 }],
      images: [{ mimeType: "image/png", bufferView: imgView }],
      accessors: [
        { bufferView: posView, componentType: 5126, count: mesh.positions.length / 3, type: "VEC3", min: mesh.min, max: mesh.max },
        { bufferView: nrmView, componentType: 5126, count: mesh.normals.length / 3, type: "VEC3" },
        { bufferView: uvView, componentType: 5126, count: mesh.uvs.length / 2, type: "VEC2" },
        { bufferView: idxView, componentType: 5123, count: mesh.indices.length, type: "SCALAR" }
      ],
      bufferViews: views,
      buffers: [{ byteLength: offset }]
    };

    var jsonBytes = pad4(new TextEncoder().encode(JSON.stringify(json)), 0x20);
    var bin = concat([pos, nrm, uv, idx, pngPad]);
    var total = 12 + 8 + jsonBytes.length + 8 + bin.length;
    var glb = concat([
      u32(0x46546C67),
      u32(2),
      u32(total),
      u32(jsonBytes.length),
      u32(0x4E4F534A),
      jsonBytes,
      u32(bin.length),
      u32(0x004E4942),
      bin
    ]);
    return new Blob([glb], { type: "model/gltf-binary" });
  }

  function crc32(data) {
    var table = crc32.table;
    if (!table) {
      table = crc32.table = new Uint32Array(256);
      for (var i = 0; i < 256; i++) {
        var c = i;
        for (var k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        table[i] = c >>> 0;
      }
    }
    var crc = 0xFFFFFFFF;
    for (var i = 0; i < data.length; i++) crc = table[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
    return (crc ^ 0xFFFFFFFF) >>> 0;
  }

  function writeU16(view, offset, value) {
    view.setUint16(offset, value, true);
  }

  function writeU32(view, offset, value) {
    view.setUint32(offset, value, true);
  }

  function zipUncompressed(files) {
    var encoder = new TextEncoder();
    var locals = [];
    var centrals = [];
    var offset = 0;
    var i;
    for (i = 0; i < files.length; i++) {
      var nameBytes = encoder.encode(files[i].name);
      var data = files[i].data;
      var extraLen = (64 - ((offset + 30 + nameBytes.length) % 64)) % 64;
      if (extraLen > 0 && extraLen < 4) extraLen += 64;
      var checksum = crc32(data);
      var local = new Uint8Array(30 + nameBytes.length + extraLen + data.length);
      var view = new DataView(local.buffer);
      writeU32(view, 0, 0x04034b50);
      writeU16(view, 4, 20);
      writeU16(view, 6, 0);
      writeU16(view, 8, 0);
      writeU16(view, 10, 0);
      writeU16(view, 12, 0);
      writeU32(view, 14, checksum);
      writeU32(view, 18, data.length);
      writeU32(view, 22, data.length);
      writeU16(view, 26, nameBytes.length);
      writeU16(view, 28, extraLen);
      local.set(nameBytes, 30);
      if (extraLen >= 4) {
        local[30 + nameBytes.length] = 0x55;
        local[30 + nameBytes.length + 1] = 0x5A;
        local[30 + nameBytes.length + 2] = (extraLen - 4) & 0xFF;
        local[30 + nameBytes.length + 3] = ((extraLen - 4) >> 8) & 0xFF;
      }
      local.set(data, 30 + nameBytes.length + extraLen);

      var central = new Uint8Array(46 + nameBytes.length);
      var cv = new DataView(central.buffer);
      writeU32(cv, 0, 0x02014b50);
      writeU16(cv, 4, 20);
      writeU16(cv, 6, 20);
      writeU16(cv, 8, 0);
      writeU16(cv, 10, 0);
      writeU16(cv, 12, 0);
      writeU16(cv, 14, 0);
      writeU32(cv, 16, checksum);
      writeU32(cv, 20, data.length);
      writeU32(cv, 24, data.length);
      writeU16(cv, 28, nameBytes.length);
      writeU16(cv, 30, 0);
      writeU16(cv, 32, 0);
      writeU16(cv, 34, 0);
      writeU16(cv, 36, 0);
      writeU32(cv, 38, 0);
      writeU32(cv, 42, offset);
      central.set(nameBytes, 46);

      locals.push(local);
      centrals.push(central);
      offset += local.length;
    }

    var cdOffset = offset;
    var cdSize = 0;
    for (i = 0; i < centrals.length; i++) cdSize += centrals[i].length;
    var eocd = new Uint8Array(22);
    var ev = new DataView(eocd.buffer);
    writeU32(ev, 0, 0x06054b50);
    writeU16(ev, 4, 0);
    writeU16(ev, 6, 0);
    writeU16(ev, 8, files.length);
    writeU16(ev, 10, files.length);
    writeU32(ev, 12, cdSize);
    writeU32(ev, 16, cdOffset);
    writeU16(ev, 20, 0);

    var total = cdOffset + cdSize + eocd.length;
    var out = new Uint8Array(total);
    var pos = 0;
    for (i = 0; i < locals.length; i++) { out.set(locals[i], pos); pos += locals[i].length; }
    for (i = 0; i < centrals.length; i++) { out.set(centrals[i], pos); pos += centrals[i].length; }
    out.set(eocd, pos);
    return out;
  }

  function buildUsda(spec) {
    var n = spec.panels || 1;
    var h = (spec.metres.h / 2).toFixed(4);
    var d = spec.metres.d.toFixed(4);
    var counts = [];
    var indices = [];
    var points = [];
    var uvs = [];
    var normals = [];
    var i;
    var v;
    for (i = 0; i < n; i++) {
      var panelW = (n > 1 ? spec.panelOuterCm.w : spec.outerCm.w) / 100;
      var gap = (spec.gapCm || 0) / 100;
      var cx = n > 1 ? (i - (n - 1) / 2) * (panelW + gap) : 0;
      var x0 = (cx - panelW / 2).toFixed(4);
      var x1 = (cx + panelW / 2).toFixed(4);
      var u0 = (i / n).toFixed(4);
      var u1 = ((i + 1) / n).toFixed(4);
      var base = i * 4;
      counts.push("4");
      indices.push(base + ", " + (base + 1) + ", " + (base + 2) + ", " + (base + 3));
      points.push("(" + x0 + ", -" + h + ", 0), (" + x1 + ", -" + h + ", 0), (" + x1 + ", " + h + ", 0), (" + x0 + ", " + h + ", 0)");
      uvs.push("(" + u0 + ", 0), (" + u1 + ", 0), (" + u1 + ", 1), (" + u0 + ", 1)");
      for (v = 0; v < 4; v++) normals.push("(0, 0, 1)");
    }
    var hw = (spec.metres.w / 2).toFixed(4);
    return [
      "#usda 1.0",
      "(",
      "    defaultPrim = \"Artwork\"",
      "    metersPerUnit = 1",
      "    upAxis = \"Y\"",
      ")",
      "",
      "def Xform \"Artwork\" (",
      "    prepend apiSchemas = [\"Preliminary_AnchoringAPI\"]",
      ")",
      "{",
      "    uniform token preliminary:anchoring:type = \"plane\"",
      "    uniform token preliminary:anchoring:alignment = \"vertical\"",
      "",
      "    def Mesh \"Print\"",
      "    {",
      "        float3[] extent = [(-" + hw + ", -" + h + ", 0), (" + hw + ", " + h + ", " + d + ")]",
      "        int[] faceVertexCounts = [" + counts.join(", ") + "]",
      "        int[] faceVertexIndices = [" + indices.join(", ") + "]",
      "        normal3f[] normals = [" + normals.join(", ") + "] (",
      "            interpolation = \"vertex\"",
      "        )",
      "        point3f[] points = [" + points.join(", ") + "]",
      "        texCoord2f[] primvars:st = [" + uvs.join(", ") + "] (",
      "            interpolation = \"vertex\"",
      "        )",
      "        uniform token subdivisionScheme = \"none\"",
      "        rel material:binding = </Artwork/Material>",
      "    }",
      "",
      "    def Material \"Material\"",
      "    {",
      "        token outputs:surface.connect = </Artwork/Material/PreviewSurface.outputs:surface>",
      "",
      "        def Shader \"PreviewSurface\"",
      "        {",
      "            uniform token info:id = \"UsdPreviewSurface\"",
      "            color3f inputs:diffuseColor.connect = </Artwork/Material/Texture.outputs:rgb>",
      "            float inputs:metallic = 0",
      "            float inputs:roughness = 0.8",
      "            token outputs:surface",
      "        }",
      "",
      "        def Shader \"Texture\"",
      "        {",
      "            uniform token info:id = \"UsdUVTexture\"",
      "            asset inputs:file = @texture.png@",
      "            token inputs:wrapS = \"clamp\"",
      "            token inputs:wrapT = \"clamp\"",
      "            token outputs:rgb",
      "        }",
      "    }",
      "}"
    ].join("\n");
  }

  function buildUsdz(png, spec) {
    var usda = new TextEncoder().encode(buildUsda(spec));
    var bytes = zipUncompressed([
      { name: "model.usda", data: usda },
      { name: "texture.png", data: png }
    ]);
    return new Blob([bytes], { type: "model/vnd.usdz+zip" });
  }

  function launchQuickLook(usdzBlob) {
    revokeModel();
    objectUrl = URL.createObjectURL(usdzBlob);
    var a = document.createElement("a");
    a.rel = "ar";
    a.href = objectUrl + "#allowsContentScaling=0";
    var img = document.createElement("img");
    img.alt = "View in your room";
    a.appendChild(img);
    a.style.cssText = "position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)";
    document.body.appendChild(a);
    a.click();
    window.setTimeout(function () {
      if (a.parentNode) a.remove();
    }, 4000);
  }

  function injectModule(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.type = "module";
      s.crossOrigin = "anonymous";
      s.src = src;
      s.onload = resolve;
      s.onerror = function () { reject(new Error("Could not load the AR viewer")); };
      document.head.appendChild(s);
    });
  }

  function waitForModelViewer(ms) {
    if (customElements.get("model-viewer")) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      var done = false;
      var timer = window.setTimeout(function () {
        if (done) return;
        done = true;
        reject(new Error("Could not load the AR viewer"));
      }, ms);
      customElements.whenDefined("model-viewer").then(function () {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        resolve();
      });
    });
  }

  function ensureModelViewer() {
    if (customElements.get("model-viewer")) return Promise.resolve();
    if (ensureModelViewer.pending) return ensureModelViewer.pending;
    var sources = [];
    if (window.theme && window.theme.modelViewerUrl) sources.push(window.theme.modelViewerUrl);
    sources.push(MODEL_VIEWER_SRC);

    ensureModelViewer.pending = (function tryNext(i) {
      if (i >= sources.length) return Promise.reject(new Error("Could not load the AR viewer"));
      return injectModule(sources[i]).then(function () {
        return waitForModelViewer(8000);
      }).catch(function () {
        return tryNext(i + 1);
      });
    }(0));
    return ensureModelViewer.pending;
  }

  function qrDataUrl(url) {
    if (typeof window.qrcode !== "function") return "";
    var qr = window.qrcode(0, "M");
    qr.addData(url);
    qr.make();
    return qr.createDataURL(8, 4);
  }

  function whenQrReady() {
    if (typeof window.qrcode === "function") return Promise.resolve();
    return new Promise(function (resolve) {
      var n = 0;
      var timer = window.setInterval(function () {
        n += 1;
        if (typeof window.qrcode === "function" || n > 40) {
          window.clearInterval(timer);
          resolve();
        }
      }, 50);
    });
  }

  function revokeModel() {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
  }

  function closeOverlay() {
    if (!overlay) return;
    overlay.remove();
    overlay = null;
    iosPlace = null;
    revokeModel();
    document.documentElement.classList.remove("view-in-room-lock");
    if (lastFocus && typeof lastFocus.focus === "function") lastFocus.focus();
    lastFocus = null;
  }

  function sizeLine(spec) {
    var size = String(spec.options.size || "").trim();
    var bits = [];
    if (size) {
      if (spec.panels > 1) {
        var name = size.replace(/\s*\([^)]*\)\s*$/g, "").trim() || size;
        bits.push(name + " \u00b7 " + Math.round(spec.outerCm.w) + "\u00d7" + Math.round(spec.outerCm.h) + "cm");
      } else {
        bits.push(size);
      }
    } else {
      bits.push(spec.outerCm.w + "\u00d7" + spec.outerCm.h + "cm");
    }
    if (!spec.frameNone) bits.push((spec.options.frameStyle || "Classic") + " \u00b7 " + spec.options.colour);
    if (spec.mounted) bits.push("Mounted");
    return bits.join(" \u00b7 ");
  }

  function setStatus(el, message, isError) {
    if (!el) return;
    el.textContent = message || "";
    el.toggleAttribute("data-error", !!isError);
  }

  function openDesktop(visualiser, spec) {
    var url = arHandoffUrl(visualiser);
    overlay.querySelector("[data-vir-desktop]").hidden = false;
    overlay.querySelector("[data-vir-mobile]").hidden = true;
    overlay.querySelector("[data-vir-title]").textContent = "View in your room";
    overlay.querySelector("[data-vir-copy]").innerHTML = spec.panels > 1
      ? "Scan the <strong>QR code</strong> to continue on your phone. Place this set on a wall at true size."
      : "Scan the <strong>QR code</strong> to continue on your phone. Place this print on a wall at true size.";
    overlay.querySelector("[data-vir-meta]").textContent = sizeLine(spec);
    var img = overlay.querySelector("[data-vir-qr]");
    var link = overlay.querySelector("[data-vir-link]");
    link.href = url;
    link.textContent = url.replace(/^https?:\/\//, "");
    overlay.querySelector("[data-vir-status]").textContent = "";
    var desktop = overlay.querySelector("[data-vir-desktop]");
    if (spec.panels > 1) {
      loadImage(spec.src).then(function (image) {
        if (!overlay) return;
        var preview = document.createElement("img");
        preview.className = "vir-preview";
        preview.alt = spec.title;
        preview.src = paintPreviewCanvas(spec, image).toDataURL("image/png");
        desktop.insertBefore(preview, desktop.firstChild);
      }).catch(function () {});
    }
    whenQrReady().then(function () {
      var qr = qrDataUrl(url);
      if (qr) {
        img.src = qr;
        img.hidden = false;
      } else {
        img.hidden = true;
      }
    });
  }

  function openMobile(visualiser, spec) {
    overlay.querySelector("[data-vir-desktop]").hidden = true;
    overlay.querySelector("[data-vir-mobile]").hidden = false;
    overlay.querySelector("[data-vir-title]").textContent = "View in your room";
    overlay.querySelector("[data-vir-copy]").textContent = isIOSNotSafari()
      ? "On iPhone, open this page in Safari to place the print on a wall."
      : spec.panels > 1
        ? "Tap Place on a wall, then point your camera at a wall. The set stays true to the size you chose."
        : "Tap Place on a wall, then point your camera at a wall. The print stays true to the size you chose.";
    overlay.querySelector("[data-vir-meta]").textContent = sizeLine(spec);
    setStatus(overlay.querySelector("[data-vir-status]"), "Preparing your print…", false);

    var viewerHost = overlay.querySelector("[data-vir-viewer]");
    viewerHost.innerHTML = "";
    iosPlace = null;

    if (isIOSDevice()) {
      openIOS(spec, viewerHost);
      return;
    }
    openAndroid(spec, viewerHost);
  }

  function openIOS(spec, viewerHost) {
    loadImage(spec.src).then(function (img) {
      var previewCanvas = paintPreviewCanvas(spec, img);
      var preview = document.createElement("img");
      preview.className = "vir-preview";
      preview.alt = spec.title;
      preview.src = previewCanvas.toDataURL("image/png");
      viewerHost.appendChild(preview);
      return canvasPng(paintModelCanvas(spec, img));
    }).then(function (png) {
      iosPlace = { spec: spec, png: png };
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "vir-place-btn";
      btn.setAttribute("data-vir-place", "true");
      btn.textContent = "Place on a wall";
      viewerHost.appendChild(btn);
      setStatus(overlay.querySelector("[data-vir-status]"), "Tap Place on a wall, then aim at a blank wall.", false);
    }).catch(function (error) {
      setStatus(overlay.querySelector("[data-vir-status]"), (error && error.message) || "Could not start AR.", true);
    });
  }

  function placeIOS() {
    if (!iosPlace) return;
    if (isIOSNotSafari()) {
      setStatus(overlay.querySelector("[data-vir-status]"), "On iPhone, open this page in Safari to place the print on a wall.", true);
      return;
    }
    try {
      launchQuickLook(buildUsdz(iosPlace.png, iosPlace.spec));
    } catch (error) {
      setStatus(overlay.querySelector("[data-vir-status]"), "Could not start AR Quick Look.", true);
    }
  }

  function openAndroid(spec, viewerHost) {
    Promise.all([loadImage(spec.src), ensureModelViewer()])
      .then(function (parts) {
        var canvas = paintModelCanvas(spec, parts[0]);
        return canvasPng(canvas);
      })
      .then(function (png) {
        revokeModel();
        var blob = buildGlb(png, spec);
        objectUrl = URL.createObjectURL(blob);
        var viewer = document.createElement("model-viewer");
        viewer.setAttribute("src", objectUrl);
        viewer.setAttribute("alt", spec.title + " at " + spec.outerCm.w + " by " + spec.outerCm.h + " centimetres");
        viewer.setAttribute("ar", "");
        viewer.setAttribute("ar-modes", "webxr");
        viewer.setAttribute("ar-placement", "wall");
        viewer.setAttribute("ar-scale", "fixed");
        viewer.setAttribute("camera-controls", "");
        viewer.setAttribute("touch-action", "pan-y");
        viewer.setAttribute("shadow-intensity", "0.55");
        viewer.setAttribute("exposure", "1.05");
        viewer.setAttribute("interaction-prompt", "none");
        var arButton = document.createElement("button");
        arButton.setAttribute("slot", "ar-button");
        arButton.className = "vir-ar-btn";
        arButton.type = "button";
        arButton.textContent = "Place on a wall";
        viewer.appendChild(arButton);
        viewerHost.appendChild(viewer);
        setStatus(overlay.querySelector("[data-vir-status]"), "Tap Place to put this print on a wall.", false);
        viewer.addEventListener("ar-status", function (event) {
          if (event.detail && event.detail.status === "failed") {
            setStatus(overlay.querySelector("[data-vir-status]"), "AR needs Chrome on Android.", true);
          }
        });
      })
      .catch(function (error) {
        setStatus(overlay.querySelector("[data-vir-status]"), (error && error.message) || "Could not start AR.", true);
      });
  }

  function ensureOverlay() {
    if (overlay) return overlay;
    overlay = document.createElement("div");
    overlay.className = "vir-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "vir-title");
    overlay.innerHTML =
      '<div class="vir-scrim" data-vir-close="true"></div>' +
      '<div class="vir-card" role="document">' +
        '<button type="button" class="vir-close" data-vir-close="true" aria-label="Close">×</button>' +
        '<div class="vir-icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.6">' +
            '<path d="M8 3H4v4M16 3h4v4M8 21H4v-4M16 21h4v-4"/>' +
            '<path d="M8 8h8v8H8z"/>' +
            '<path d="M10 12h4"/>' +
          "</svg>" +
        "</div>" +
        '<h2 id="vir-title" data-vir-title>View in your room</h2>' +
        '<p class="vir-copy" data-vir-copy></p>' +
        '<p class="vir-meta" data-vir-meta></p>' +
        '<div class="vir-desktop" data-vir-desktop>' +
          '<img class="vir-qr" data-vir-qr alt="QR code for this print on your phone" hidden>' +
          '<p class="vir-mobile-note">Already on a phone? Open this page with <strong>Safari</strong> on iOS or <strong>Chrome</strong> on Android.</p>' +
          '<a class="vir-link" data-vir-link></a>' +
        "</div>" +
        '<div class="vir-mobile" data-vir-mobile hidden>' +
          '<div class="vir-viewer" data-vir-viewer></div>' +
        "</div>" +
        '<p class="vir-status" data-vir-status role="status"></p>' +
      "</div>";
    overlay.addEventListener("click", function (event) {
      if (event.target && event.target.getAttribute("data-vir-close")) closeOverlay();
      if (event.target && event.target.closest("[data-vir-place]")) {
        event.preventDefault();
        placeIOS();
      }
    });
    document.body.appendChild(overlay);
    return overlay;
  }

  function openViewInRoom(from) {
    var root = productRoot(from);
    var visualiser = visualiserOf(root);
    if (!visualiser) return;
    var spec = specFromVisualiser(visualiser);
    lastFocus = document.activeElement;
    ensureOverlay();
    document.documentElement.classList.add("view-in-room-lock");
    overlay.querySelector(".vir-card").classList.toggle("vir-card--wide", spec.panels > 1);
    overlay.querySelector(".vir-close").focus();

    if (spec.error) {
      overlay.querySelector("[data-vir-desktop]").hidden = true;
      overlay.querySelector("[data-vir-mobile]").hidden = true;
      overlay.querySelector("[data-vir-title]").textContent = "View in your room";
      overlay.querySelector("[data-vir-copy]").textContent = spec.error;
      overlay.querySelector("[data-vir-meta]").textContent = "";
      overlay.querySelector("[data-vir-status]").textContent = "";
      return;
    }

    if (isHandheldAR()) openMobile(visualiser, spec);
    else openDesktop(visualiser, spec);
  }

  function wantsAutoAR() {
    try {
      return new URLSearchParams(location.search).get("ar") === "1";
    } catch (error) {
      return false;
    }
  }

  function boot() {
    var root = document.querySelector("[data-product]");
    if (!root) return;

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && overlay) {
        event.preventDefault();
        closeOverlay();
      }
    });

    root.addEventListener("click", function (event) {
      var btn = event.target.closest("[data-view-in-room]");
      if (!btn) return;
      event.preventDefault();
      openViewInRoom(btn);
    });

    if (wantsAutoAR()) {
      window.setTimeout(function () {
        openViewInRoom(root);
      }, 280);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
